import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { toast } from 'sonner';
import {
  Users,
  Target,
  BookOpen,
  BarChart3,
  CheckCircle,
    Trash2,
  Edit
} from 'lucide-react';
import { coursesApi, exercisesApi } from '@/services/api';
import CreateExerciseDialog from '@/components/CreateExerciseDialog';

interface Course {
  id: string;
  title: string;
}

interface ExerciseStats {
  totalExercises: number;
  totalAttempts: number;
  averageScore: number;
  popularExercises: Array<{
    id: string;
    title: string;
    _count: {
      quizAttempts: number;
    };
  }>;
}


export const ExercisesPage: React.FC = () => {
  const [isCreateExerciseDialogOpen, setIsCreateExerciseDialogOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [editingExercise, setEditingExercise] = useState<any>(null);

  const queryClient = useQueryClient();

  // Debug auth
  React.useEffect(() => {
    const token = localStorage.getItem('token');
    console.log('🔑 Auth token exists:', !!token, token ? 'Token length: ' + token.length : 'No token');
    console.log('🔑 Full token:', token ? token.substring(0, 50) + '...' : 'No token');
  }, []);

  
  // Fetch courses for dropdown
  const { data: courses = [] } = useQuery<Course[]>(
    'courses',
    async () => {
      console.log('🔵 Fetching courses...');
      const response = await coursesApi.getAll();
      console.log('🔵 Courses response:', response.data);
      return response.data.data?.courses || response.data.courses || [];
    },
    {
      onSuccess: (data) => {
        console.log('🟢 Courses loaded successfully:', data);
      },
      onError: (error) => {
        console.error('🔴 Error fetching courses:', error);
      }
    }
  );

  // Fetch independent exercises (QUIZ type lessons)
  const { data: exercises = [], isLoading: isLoadingExercises, refetch: refetchExercises } = useQuery(
    ['exercises', selectedCourse],
    async () => {
      console.log('🔄 Fetching exercises with params:', { courseId: selectedCourse });
      // Temporalmente obtener TODOS los ejercicios para debug
      const params = {}; // selectedCourse ? { courseId: selectedCourse } : {};
      const response = await exercisesApi.getAll(params);
      console.log('✅ Exercises API response:', response.data);
      console.log('📊 Exercises count:', Array.isArray(response.data) ? response.data.length : 'Not an array');
      return response.data || [];
    },
    {
      onSuccess: (data) => {
        console.log('🟢 Exercises loaded successfully:', data);
        console.log('📈 Final exercises count:', Array.isArray(data) ? data.length : 'Not an array');
      },
      onError: (error) => {
        console.error('🔴 Error fetching exercises:', error);
        toast.error('Error al cargar ejercicios independientes');
      }
    }
  );

  // Fetch exercise statistics
  const { data: stats } = useQuery<ExerciseStats>(
    'exercise-stats',
    async () => {
      const response = await exercisesApi.getStats();
      return response.data;
    },
    {
      onError: (error) => {
        console.error('Error fetching stats:', error);
      }
    }
  );

  // Delete exercise function
  const handleDeleteExercise = async (exerciseId: string, title: string) => {
    if (!confirm(`¿Estás seguro de que deseas eliminar el ejercicio "${title}"?`)) {
      return;
    }

    try {
      await exercisesApi.delete(exerciseId);
      toast.success('Ejercicio eliminado correctamente');
      refetchExercises();
      queryClient.invalidateQueries('exercise-stats');
    } catch (error) {
      console.error('Error deleting exercise:', error);
      toast.error('Error al eliminar el ejercicio');
    }
  };

  // Edit exercise function
  const handleEditExercise = async (exercise: any) => {
    try {
      // Load full exercise data with questions
      const response = await exercisesApi.getById(exercise.id);
      const fullExercise = response.data.data;

      setEditingExercise({
        id: fullExercise.id,
        title: fullExercise.title,
        description: fullExercise.description,
        courseId: fullExercise.courseId,
        questions: fullExercise.questions
      });
      setIsCreateExerciseDialogOpen(true);
    } catch (error) {
      console.error('Error loading exercise for edit:', error);
      toast.error('Error al cargar el ejercicio para editar');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Ejercicios y Quizzes</h1>
          <p className="text-muted-foreground">
            Agrega preguntas opcionales a lecciones de video o crea ejercicios independientes
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => {
              setEditingExercise(null);
              setIsCreateExerciseDialogOpen(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Target className="h-4 w-4 mr-2" />
            Crear Quiz Independiente
          </Button>
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="px-3 py-2 border border-input rounded-lg bg-background text-foreground"
          >
            <option value="">Todos los cursos</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="course-filter">Filtrar por Curso</Label>
              <select
                id="course-filter"
                className="w-full px-3 py-2 border border-input rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              >
                <option value="all">Todos los cursos</option>
                {Array.isArray(courses) && courses.map((course) => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="status-filter">Estado</Label>
              <select
                id="status-filter"
                className="w-full px-3 py-2 border border-input rounded-lg shadow-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
              >
                <option value="all">Todos</option>
                <option value="active">Activos</option>
                <option value="inactive">Inactivos</option>
              </select>
            </div>
            <div>
              <Label htmlFor="search">Buscar</Label>
              <Input placeholder="Buscar ejercicios..." />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Ejercicios</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalExercises || 0}</div>
              <p className="text-xs text-muted-foreground">
                Ejercicios creados en total
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Intentos</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalAttempts || 0}</div>
              <p className="text-xs text-muted-foreground">
                Intentos de estudiantes
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Promedio General</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{Math.round(stats.averageScore || 0)}%</div>
              <p className="text-xs text-muted-foreground">
                Puntuación promedio
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Más Popular</CardTitle>
              <BookOpen className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-sm font-medium truncate">
                {stats.popularExercises?.[0]?.title || 'Ninguno'}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.popularExercises?.[0]?._count?.quizAttempts || 0} intentos
              </p>
            </CardContent>
          </Card>
        </div>
      )}

  
      {/* Independent Exercises Table */}
      <Card>
        <CardHeader>
          <CardTitle>Ejercicios Independientes</CardTitle>
          <p className="text-sm text-muted-foreground">
            Ejercicios de tipo QUIZ que funcionan como lecciones independientes
          </p>
        </CardHeader>
        <CardContent>
          {isLoadingExercises ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">Cargando ejercicios...</div>
            </div>
          ) : !Array.isArray(exercises) || exercises.length === 0 ? (
            <div className="text-center py-12">
              <div className="flex flex-col items-center">
                <div className="rounded-full bg-orange-100 p-3 mb-4">
                  <Target className="h-8 w-8 text-orange-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay ejercicios independientes</h3>
                <p className="text-sm text-gray-500 mb-6 max-w-sm">
                  Crea tu primer ejercicio independiente haciendo clic en "Crear Quiz Independiente".
                </p>
                <Button
                  onClick={() => {
                      setEditingExercise(null);
                      setIsCreateExerciseDialogOpen(true);
                    }}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Target className="h-4 w-4 mr-2" />
                  Crear Quiz Independiente
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ejercicio</TableHead>
                  <TableHead>Curso</TableHead>
                  <TableHead>Preguntas</TableHead>
                  <TableHead>Intentos</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(exercises) && exercises.map((exercise: any) => (
                  <TableRow key={exercise.id}>
                    <TableCell>
                      <div className="min-w-0">
                        <div className="font-medium text-gray-900 truncate">{exercise.title}</div>
                        {exercise.description && (
                          <div className="text-sm text-gray-500 truncate max-w-[300px]">
                            {exercise.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-orange-50 text-orange-700 border-orange-200">
                        {exercise.course?.title || 'Sin curso'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <div className="bg-green-100 text-green-800 rounded-full px-2 py-1 text-xs font-medium">
                          <CheckCircle className="inline w-3 h-3 mr-1" />
                          {exercise.questions?.length || 0} preguntas
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600">
                        {exercise._count?.quizAttempts || 0} intentos
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end space-x-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditExercise(exercise)}
                          className="h-10 w-10 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          title="Editar ejercicio"
                        >
                          <Edit className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteExercise(exercise.id, exercise.title)}
                          className="h-10 w-10 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Eliminar ejercicio"
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) || null}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      
      {/* Create Exercise Dialog */}
      <CreateExerciseDialog
        isOpen={isCreateExerciseDialogOpen}
        onClose={() => {
          setIsCreateExerciseDialogOpen(false);
          setEditingExercise(null);
        }}
        courses={courses}
        editingExercise={editingExercise}
      />
    </div>
  );
};
